import React, { FC, ReactNode, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { useRouter } from '@blocs.router';
import { PermissionsProvider } from '@minecraft.permissions';
import { Loading } from '@minecraft.loader';
import { ChatSupportProvider } from '@blocs.chat-support';
import { AuthLanguageSetter } from '../IntlFeature';
import { useUserContext } from '../UserContext';
import { ApplyCoupon } from './ApplyCoupon';
import { resetPaginatedCacheQueries } from './cacheHelper';

export interface AuthenticatedWrapperProps {
  redirectToRegisterPage?: boolean;
  shouldHideChatSupport?: boolean;
  children: ReactNode;
}

/**
 * Provides standard auth checks and authenticated-only providers for private routes.
 * Wrap authenticated pages in this component when using Route separately or the RenderRoutes pattern.
 */
const AuthenticatedWrapper: FC<AuthenticatedWrapperProps> = ({
  children,
  redirectToRegisterPage = false,
  shouldHideChatSupport = false,
}) => {
  const { isAuthenticated, isAuthenticating, loginError, verifyTokens } = useUserContext();
  const { cache } = useApolloClient();
  const { pathname, search } = useRouter();
  const queryParams = new URLSearchParams(search);
  const redirectToRegistration = queryParams.get('redirectToRegistration');

  useEffect(() => {
    // this resets cached queries of lists of data
    // when we navigate to a new page
    // so that any mutations will be correctly
    // refetched and displayed if we return
    resetPaginatedCacheQueries({ cache });
  }, [cache, pathname]);

  useEffect(() => {
    if (!isAuthenticated && !isAuthenticating && !loginError) {
      verifyTokens({ redirectToTalentRegistration: redirectToRegisterPage || redirectToRegistration }).catch((e) => {
        console.error('verify tokens error', { error: e });
      });
    }
  }, [isAuthenticated, isAuthenticating, loginError, redirectToRegisterPage, redirectToRegistration, verifyTokens]);

  if (loginError || !isAuthenticated) {
    return (
      <div
        data-testid="authenticated-route-loading-or-error"
        style={{ display: 'flex', alignItems: 'center', width: '80vw', height: '80vh', margin: '0 auto' }}
      >
        <Loading />
        &nbsp;
      </div>
    );
  }

  return (
    <PermissionsProvider>
      <ChatSupportProvider shouldHide={shouldHideChatSupport} />
      <AuthLanguageSetter>
        <ApplyCoupon>{children}</ApplyCoupon>
      </AuthLanguageSetter>
    </PermissionsProvider>
  );
};

export default AuthenticatedWrapper;
